import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterSelect,
  cn,
  type DataTableColumn,
} from '@wundo/ui';
import type {
  NotificationHistoryKind,
  NotificationSendSummary,
} from '@wundo/shared/notificationHistory.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import {
  listNotificationSendKinds,
  listNotificationSends,
} from '@/lib/notificationHistory.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import {
  CHANNEL_OPTIONS,
  DEFAULT_HISTORY_FILTERS,
  NO_HISTORY_FILTERS,
  SENT_BY_OPTIONS,
  toNotificationSendFilters,
  typeOptions,
  type HistoryFilters,
  type TypeFilter,
} from './historyFilters.ts';
import { HISTORY_CHANNEL_LABELS, countParts, sendDate } from './historyLabels.ts';

function channelsOf(send: NotificationSendSummary): string {
  return send.channels.map((channel) => HISTORY_CHANNEL_LABELS[channel]).join(', ');
}

/* A press to people of several organisations belongs to none of them. */
function organisationOf(send: NotificationSendSummary): string {
  return send.organisation_name ?? 'Several organisations';
}

/* Only with every organisation listed: with one, it is the header's. */
const ORGANISATION_COLUMN: DataTableColumn<NotificationSendSummary> = {
  key: 'organisation',
  header: 'Organisation',
  hideBelow: 'mobile',
  render: (send) => (
    <span className={cn(!send.organisation_name && 'text-ink-muted')}>
      {organisationOf(send)}
    </span>
  ),
};

function senderOf(send: NotificationSendSummary): string {
  return send.sent_by ? (send.sent_by.name ?? 'Unknown staff member') : 'Automatic';
}

/* A press in the console has a time; a day of automatic sends only has its day. */
function dateOf(send: NotificationSendSummary): string {
  return sendDate(send.sent_at, send.sent_by !== null);
}

export function SendOutcome({ send }: { send: NotificationSendSummary }) {
  const parts = countParts(send.counts);
  if (parts.length === 0) return <span className='text-ink-muted'>—</span>;

  return (
    <span>
      {parts.map((part, index) => (
        <span
          key={part.key}
          className={cn('whitespace-nowrap', part.problem && 'font-semibold text-danger')}
        >
          {index > 0 && <span className='font-normal text-ink-muted'> · </span>}
          {part.text}
        </span>
      ))}
    </span>
  );
}

/**
 * Everything Wundo sent to the people of the organisation in the header, or of
 * every organisation: each press in the console, and each day of what goes out
 * on its own. A row opens the send, with everyone it was meant for.
 */
export function HistoryTab() {
  const organisationId = useOrganisationFilter()?.id ?? null;
  const [filters, setFilters] = useState(DEFAULT_HISTORY_FILTERS);

  return (
    <HistoryList
      key={organisationId ?? 'all'}
      organisationId={organisationId}
      filters={filters}
      onFiltersChange={setFilters}
    />
  );
}

interface HistoryListProps {
  organisationId: string | null;
  filters: HistoryFilters;
  onFiltersChange: (filters: HistoryFilters) => void;
}

function HistoryList({ organisationId, filters, onFiltersChange }: HistoryListProps) {
  const allOrganisations = organisationId === null;
  const router = useRouter();
  const [page, setPage] = useState(1);

  const kinds = useQuery({
    queryKey: ['notificationSendKinds'],
    queryFn: listNotificationSendKinds,
    staleTime: Infinity,
  });

  const sends = useQuery({
    queryKey: ['notificationSends', organisationId, filters, page],
    queryFn: () =>
      listNotificationSends(
        organisationId,
        toNotificationSendFilters(filters),
        page,
        PAGE_SIZE,
      ),
    placeholderData: keepPreviousData,
  });

  /* A narrower list is a different list: it starts at its first page. */
  const changeFilters = (next: Partial<HistoryFilters>) => {
    onFiltersChange({ ...filters, ...next });
    setPage(1);
  };

  return (
    <DataTable<NotificationSendSummary>
      label='Notification history'
      columns={historyColumns(allOrganisations)}
      rows={sends.data?.data ?? []}
      rowKey={(send) => send.id}
      loading={sends.isFetching}
      rowHref={(send) => `/admin/notifications/history/${send.id}`}
      onNavigate={(href) => void router.navigate({ href })}
      pagination={{ page, pageSize: PAGE_SIZE, total: sends.data?.meta.totalItems ?? 0 }}
      onPageChange={setPage}
      toolbar={
        <HistoryFilterBar
          kinds={kinds.data ?? []}
          filters={filters}
          onChange={changeFilters}
        />
      }
      error={
        sends.isError ? (
          <span className='flex flex-col items-center gap-2'>
            The history did not load.
            <Button variant='quiet' size='sm' onClick={() => void sends.refetch()}>
              Retry
            </Button>
          </span>
        ) : undefined
      }
      emptyState={
        <HistoryEmptyState
          filters={filters}
          allOrganisations={allOrganisations}
          onShowEverything={() => changeFilters(NO_HISTORY_FILTERS)}
        />
      }
    />
  );
}

function historyColumns(
  allOrganisations: boolean,
): DataTableColumn<NotificationSendSummary>[] {
  return [
    {
      key: 'label',
      header: 'Notification',
      render: (send) => (
        <span className='inline-flex flex-wrap items-center gap-x-2 gap-y-1'>
          <span>{send.label}</span>
          {!send.content_kept && <Badge tone='quiet'>No content kept</Badge>}
        </span>
      ),
      mobileSubline: (send) =>
        [
          ...(allOrganisations ? [organisationOf(send)] : []),
          channelsOf(send),
          senderOf(send),
          `${send.recipient_count} ${send.recipient_count === 1 ? 'person' : 'people'}`,
          dateOf(send),
        ].join(' · '),
    },
    ...(allOrganisations ? [ORGANISATION_COLUMN] : []),
    {
      key: 'channels',
      header: 'Channel',
      hideBelow: 'mobile',
      render: channelsOf,
    },
    {
      key: 'sent_by',
      header: 'Sent by',
      hideBelow: 'mobile',
      render: (send) => (
        <span className={cn(!send.sent_by && 'text-ink-muted')}>{senderOf(send)}</span>
      ),
    },
    {
      key: 'recipient_count',
      header: 'Recipients',
      align: 'end',
      hideBelow: 'mobile',
      render: (send) => send.recipient_count,
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (send) => <SendOutcome send={send} />,
    },
    {
      key: 'sent_at',
      header: 'Date',
      nowrap: true,
      hideBelow: 'mobile',
      render: dateOf,
    },
  ];
}

interface HistoryFilterBarProps {
  kinds: NotificationHistoryKind[];
  filters: HistoryFilters;
  onChange: (next: Partial<HistoryFilters>) => void;
}

function HistoryFilterBar({ kinds, filters, onChange }: HistoryFilterBarProps) {
  return (
    <>
      <FilterSelect<TypeFilter>
        label='Type'
        options={typeOptions(kinds)}
        value={filters.type}
        onValueChange={(type) => onChange({ type })}
      />
      <FilterSelect<HistoryFilters['channel']>
        label='Channel'
        options={CHANNEL_OPTIONS}
        value={filters.channel}
        onValueChange={(channel) => onChange({ channel })}
      />
      <FilterSelect<HistoryFilters['sentBy']>
        label='Sent by'
        options={SENT_BY_OPTIONS}
        value={filters.sentBy}
        onValueChange={(sentBy) => onChange({ sentBy })}
      />
    </>
  );
}

interface HistoryEmptyStateProps {
  filters: HistoryFilters;
  allOrganisations: boolean;
  onShowEverything: () => void;
}

function HistoryEmptyState({
  filters,
  allOrganisations,
  onShowEverything,
}: HistoryEmptyStateProps) {
  const filtered =
    filters.type !== NO_HISTORY_FILTERS.type ||
    filters.channel !== NO_HISTORY_FILTERS.channel ||
    filters.sentBy !== NO_HISTORY_FILTERS.sentBy;

  if (!filtered) {
    return (
      <EmptyState
        title='Nothing sent yet'
        description={`What Wundo sends to ${allOrganisations ? 'anybody' : 'the people of this organisation'} shows up here: sends from this console, and what goes out on its own.`}
      />
    );
  }

  return (
    <EmptyState
      title='Nothing matches these filters'
      description={
        filters.type === DEFAULT_HISTORY_FILTERS.type
          ? 'Meeting notifications are left out until you pick them under Type.'
          : undefined
      }
      action={
        <Button variant='quiet' size='sm' onClick={onShowEverything}>
          Show everything
        </Button>
      }
    />
  );
}
